import { useEffect, useRef, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase, type Profile } from './lib/supabase.ts'
import { claimInvitation, type ClaimResult } from './lib/api.ts'
import { resolveView } from './lib/sessionView.ts'
import { can, canAnySettings, roleLabel } from './lib/roles.ts'
import { Login } from './pages/Login.tsx'
import { Catalog } from './pages/Catalog.tsx'
import { Upload } from './pages/Upload.tsx'
import { Settings } from './pages/Admin.tsx'
import { AccessLogs } from './pages/AccessLogs.tsx'
import { SettingsDialog } from './components/SettingsDialog.tsx'

type Tab = 'catalog' | 'upload' | 'logs'

export function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [ready, setReady] = useState(false)
  const [tab, setTab] = useState<Tab>('catalog')
  const [claim, setClaim] = useState<ClaimResult | null>(null)
  const [claimError, setClaimError] = useState<string | null>(null)
  // どの auth ユーザーについてプロフィールの解決が終わったか。
  // これが現在のセッションと一致するまでは「権限が無い」と断定しない。
  const [resolvedFor, setResolvedFor] = useState<string | null>(null)
  const resolvedRef = useRef<string | null>(null)
  // 設定はページではなくダイアログ。カタログを背後に残したまま重ねる
  // （ページを切り替えると「設定にいる」ことが分かりにくかった）。
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      if (!data.session) setReady(true)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      if (!next) {
        setProfile(null)
        setReady(true)
        resolvedRef.current = null
        setResolvedFor(null)
      }
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!session) return
    const uid = session.user.id
    // onAuthStateChange はトークン更新でも発火する。
    // 解決済みのユーザーなら引き直さない（1時間ごとに再取得しても無駄）。
    if (resolvedRef.current === uid) return
    let cancelled = false
    ;(async () => {
      const fetchProfile = async () =>
        (
          await supabase
            .from('users')
            .select('id,email,display_name,role,org_id')
            .eq('auth_user_id', session.user.id)
            .maybeSingle()
        ).data as Profile | null

      // 招待されていないユーザーはここが 0 件になる（RLS ではなく行そのものが無い）
      let row = await fetchProfile()

      // 0 件でも、まだ諦めない。
      //
      // プロフィールは本来 auth ユーザー作成時のトリガが作るが、
      // **招待より先に auth ユーザーができていると発火しない**。
      // その状態を自力で解消する（冪等なので何度通っても害は無い）。
      if (!row) {
        // 失敗を握り潰さないこと。握り潰すと「招待が無い」と
        // 「サーバ側で失敗した」が同じ画面に化けて、原因が追えなくなる。
        const result = await claimInvitation().catch((e) => {
          console.error('claim failed', e)
          setClaimError(e instanceof Error ? e.message : String(e))
          return null
        })
        if (cancelled) return
        setClaim(result)
        if (result?.linked) row = await fetchProfile()
      }

      if (cancelled) return
      setProfile(row ?? null)
      resolvedRef.current = uid
      setResolvedFor(uid)
      setReady(true)
    })()
    return () => {
      cancelled = true
    }
  }, [session])

  // 判定は sessionView.ts に切り出してある（回帰テストで固定するため）
  const view = resolveView({
    ready,
    sessionUserId: session?.user.id ?? null,
    resolvedFor,
    hasProfile: profile !== null,
  })

  if (view === 'loading' || view === 'resolving') return <p className="muted pad">読み込み中…</p>
  if (view === 'login' || !session) return <Login />

  if (view === 'denied' || !profile) {
    // 引き当てまで試して駄目だった、が確定した状態。
    // 一番多い原因は「招待したのと違うメールアドレスでログインしている」なので、
    // 実際に認証に使われたアドレスを目立たせる。
    const signedInAs = claim?.email ?? session.user.email
    return (
      <div className="pad">
        <h2>アクセス権がありません</h2>
        {claimError && (
          <p className="error small">
            サーバ側の処理に失敗しました（{claimError}）。担当者にこの表示をそのままお伝えください。
          </p>
        )}
        <p>
          このアドレスあての招待が見つかりませんでした。
          <br />
          ログインに使われたアドレス: <strong className="mono">{signedInAs}</strong>
        </p>
        <p className="muted small">
          招待を受け取ったアドレスと違っていませんか。違う場合は、一度ログアウトして
          招待されたアドレスでログインし直してください。
          同じはずであれば、このアドレスをそのまま担当者にお伝えください。
        </p>
        <p>
          <button onClick={() => supabase.auth.signOut()}>ログアウト</button>{' '}
          <button className="link" onClick={() => window.location.reload()}>
            もう一度確認する
          </button>
        </p>
      </div>
    )
  }

  // ナビの出し分けは権限単位で行う（ロールの完全一致比較を散らさない）。
  // 隠すのは操作性のためで、権限の担保は API と RLS にある。
  const role = profile.role

  return (
    <div className="app">
      <header>
        <div className="brand">素材ライブラリ</div>
        <nav>
          <button
            className={tab === 'catalog' ? 'tab active' : 'tab'}
            aria-current={tab === 'catalog' ? 'page' : undefined}
            onClick={() => setTab('catalog')}
          >
            カタログ
          </button>
          {can(role, 'content.write') && (
            <button
              className={tab === 'upload' ? 'tab active' : 'tab'}
              aria-current={tab === 'upload' ? 'page' : undefined}
              onClick={() => setTab('upload')}
            >
              アップロード
            </button>
          )}
          {can(role, 'logs.read') && (
            <button
              className={tab === 'logs' ? 'tab active' : 'tab'}
              aria-current={tab === 'logs' ? 'page' : undefined}
              onClick={() => setTab('logs')}
            >
              アクセスログ
            </button>
          )}
        </nav>
        <div className="account">
          <span className="small">
            {profile.display_name ?? profile.email}
            <span className={`role role-${profile.role}`}>{roleLabel(profile.role)}</span>
          </span>
          <button className="link" onClick={() => supabase.auth.signOut()}>
            ログアウト
          </button>
        </div>
      </header>

      <main>
        {tab === 'catalog' ? (
          // 設定はヘッダーではなくカタログのサイドバー下部（歯車）から開く
          <Catalog
            profile={profile}
            onOpenSettings={canAnySettings(role) ? () => setSettingsOpen(true) : undefined}
          />
        ) : tab === 'upload' ? (
          <Upload />
        ) : (
          <AccessLogs profile={profile} />
        )}
      </main>

      {/* 閉じるとフォーカスは開く前の要素（歯車）へ自動で戻る（<dialog> の仕様） */}
      {settingsOpen && (
        <SettingsDialog onClose={() => setSettingsOpen(false)}>
          <Settings profile={profile} />
        </SettingsDialog>
      )}
    </div>
  )
}
